import { forwardRef, useEffect, useState, useMemo, Fragment } from 'react';
import {
    useTheme,
    experimentalStyled as styled,
  } from '@material-ui/core/styles'
import { Stack, Grid, Box, Typography, Skeleton, Card } from '@material-ui/core';
import { currentFacilityId, FacilityTypes, REST_API, UNITS } from 'src/constants/Defaultvalues';
import useCompanyProfile from 'src/hooks/useCompanyProfile';
import moment from 'moment';
import { formatDateOnly } from 'src/utils/dateOnly';
import { empty } from 'src/Helpers/Common';
import { fUSPrice } from 'src/utils/formatNumber';
import { getGroupInstallNotesDisplay } from 'src/pages/dashboard/quotation/quotationGroupingHelpers';
import { buildGroupedDesignOrderDisplayRows } from 'src/utils/designOrderInvoiceUtils';

const DesignOrderInvoicePage = forwardRef(({
    invoiceDate,
    details,
    additionalSelectedServices,
    invoice,
    subTotal,
    groupedInvoiceProductTotal,
    groupedInvoiceInstallTotal,
    cartService,
    openInvoice = true,
    invoiceSelectedServices,
    data,
    userType,
    labourInstallService,
    hasViewCostPermission = true, taxRateData = [], taxAmount = 0,
    customerOrderProductGroups = [],
    designOrderProducts = [],
    markupType: invoiceMarkupTypeProp,
    groupProductMarketingValues = {},
    groupInstallMarketingValues = {},
}, ref) => {
    const showInstallPriceInPreview = details?.show_install_price !== 0;    
    const canViewCosts = !!hasViewCostPermission;

    const formattedNotes = Array.isArray(details?.notes_lines) && details.notes_lines.length
        ? details.notes_lines.join('\n')  // Join with a newline for line breaks
        : '';
    const theme = useTheme();
    const { COMPANY_LOGO } = useCompanyProfile()
    let customerProductTotal = 0;

    const groupedInvoiceDisplayRows = useMemo(() => {
        const invoiceMarkupType =
            invoiceMarkupTypeProp != null && invoiceMarkupTypeProp !== ''
                ? Number(invoiceMarkupTypeProp)
                : Number(details?.markup_type ?? 2) || 2

        const sourceProducts =
            (designOrderProducts?.length ? designOrderProducts : invoice?.cust_product) || []

        const rows = buildGroupedDesignOrderDisplayRows({
            designOrderProducts: sourceProducts,
            customerOrderProductGroups,
            markupType: invoiceMarkupType,
            groupProductMarketingValues,
            groupInstallMarketingValues,
        })

        const isMaterialRow = (row) =>
            row?.__type === 'group'
                ? Number(row?.representative?.material) === 1
                : Number(row?.material) === 1
        return rows.sort((a, b) => Number(isMaterialRow(a)) - Number(isMaterialRow(b)))
    }, [
        designOrderProducts,
        invoice?.cust_product,
        customerOrderProductGroups,
        invoiceMarkupTypeProp,
        details?.markup_type,
        groupProductMarketingValues,
        groupInstallMarketingValues,
    ])
    const [pdfs, setPdfs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (invoice?.sign_agreements) {
            // Filter out sign agreements that have a valid PDF path
            const filteredPdfs = invoice.sign_agreements
                .filter(agr => agr.agreement_pdf_path || agr.attachment) // Check if either agreement_pdf_path or attachment is not null
                .map(agr => {
                    let pdfPath = agr.agreement_pdf_path || agr.attachment;
                    if (pdfPath && !pdfPath.startsWith('http')) {
                        pdfPath = `${REST_API}${pdfPath}`;
                    }

                    // Normalize image_paths: split by comma and filter out empty/whitespace so local and live behave the same
                    const rawPaths = agr.image_paths ? agr.image_paths.split(',') : [];
                    const imagePaths = rawPaths.map(p => p.trim()).filter(Boolean);

                    return {
                        name: agr.name,
                        pdfPath: pdfPath,
                        imagePaths: imagePaths // Store the image paths in the object
                    };
                });

            setPdfs(filteredPdfs);
            setLoading(false);
        }
    }, [invoice]);
    return (
        <>
            <Box
                component="style"
                dangerouslySetInnerHTML={{
                    __html: `
                      @media print {
                        html, body, #root {
                          background: #ffffff !important;
                        }
                        .sales-order-table th,
                        .sales-order-table td {
                          border: 1px solid black !important;
                        }
                        .sales-order-table {
                          border-collapse: collapse !important;
                        }
                      }
                    `
                }}
            />
            <Stack ref={ref} style={{ background: '#ffffff', color: '#000000' }}>
                <Grid container spacing={1} sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 510, paddingRight: '6px' }}>
                            {/* <Box component="img" alt="logo" src={COMPANY_LOGO} id="logoImage" sx={{ height: 98, alignSelf: 'flex-start', filter: 'grayscale(100%)' }} /> */}
                            <Box component="img" alt="logo" src={COMPANY_LOGO} sx={{ alignSelf: 'flex-start', filter: 'grayscale(100%)', width: "250px" }} />
                            <Stack direction="column" sx={{ mt: 1, color: 'black', textTransform: 'uppercase', }}>
                                <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.address}</Typography>
                                <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.city}, {data?.state}, {data?.zipcode}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                    <Typography variant="h6" sx={{ textTransform: 'uppercase', }}>{(data?.phone_country_code || data?.phone) ? `+${data?.phone_country_code}${data?.phone}` : '951-682-9660'}</Typography>
                                    <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>|</Typography>
                                    <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.company_fax_number ? `Fax: ${data?.company_fax_number}` : ''}</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 516, }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    textTransform: 'uppercase',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'black',
                                    mb: 1,
                                    fontSize: '1.75rem'
                                }}
                            >
                                DESIGN ORDER
                            </Typography>
                            <Box
                                component="style"
                                dangerouslySetInnerHTML={{
                                    __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                }}
                            />
                            <table
                                className="sales-order-table"
                                style={{
                                    width: '100%',
                                    border: '1px solid black',
                                    borderCollapse: 'collapse',
                                    textTransform: 'uppercase',
                                    tableLayout: 'fixed'
                                }}
                            >
                                <colgroup>
                                    <col style={{ width: '50%' }} />
                                    <col style={{ width: '50%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: theme.palette.mode === 'light' ? 'black' : 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            DESIGN ORDER DATE
                                        </th>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            DESIGN ORDER ID#
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            {invoiceDate}
                                        </td>
                                        <td
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            #{details?.id || details?.doc_number || ''}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', mt: 0.2 }}>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 510, paddingRight: '6px' }}>
                            <Box
                                component="style"
                                dangerouslySetInnerHTML={{
                                    __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                }}
                            />
                            <table
                                className="sales-order-table"
                                style={{
                                    width: '100%',
                                    border: '1px solid black',
                                    borderCollapse: 'collapse',
                                    textTransform: 'uppercase'
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            CLIENT NAME AND ADDRESS (BILL TO:)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            {
                                                details?.is_split_order && details?.for_parent_facility == 1
                                                    ? FacilityTypes?.find(facility => facility.id === details?.origin_facility_id)?.label || invoice?.customer_name
                                                    : invoice?.customer_name
                                            }
                                            <br />
                                            {invoice?.customer_address || invoice?.customerAddress || ''}
                                            {!(invoice?.customer_address || invoice?.customerAddress) && (
                                              <>
                                                <br />
                                                {[invoice?.customer_city, invoice?.customer_state, invoice?.customer_zipcode].filter(Boolean).join(' ')}
                                              </>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 516 }}>
                            <Box
                                component="style"
                                dangerouslySetInnerHTML={{
                                    __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                }}
                            />

                            <>
                                {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        borderBottom: '1px solid black',
                                        borderLeft: '1px solid black',
                                        borderRight: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        marginTop: 0,
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '50%' }} />
                                        <col style={{ width: '50%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th
                                                style={{
                                                    border: '1px solid black',
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    backgroundColor: '#d9d9d9',
                                                    fontWeight: 'bold',
                                                    color: 'black',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                CLIENT CONTACT NAME
                                            </th>
                                            <th
                                                style={{
                                                    border: '1px solid black',
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    backgroundColor: '#d9d9d9',
                                                    fontWeight: 'bold',
                                                    color: 'black',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                CLIENT PHONE NUMBER
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td
                                                style={{
                                                    border: '1px solid black',
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                {invoice?.customer_contact_name || ''}
                                            </td>
                                            <td
                                                style={{
                                                    border: '1px solid black',
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                {invoice?.customer_phone || ''}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>


                            {/* PROJECT INSTALL ADDRESS */}
                            <table
                                className="sales-order-table"
                                style={{
                                    width: '100%',
                                    borderLeft: '1px solid black',
                                    borderRight: '1px solid black',
                                    borderBottom: '1px solid black',
                                    borderCollapse: 'collapse',
                                    textTransform: 'uppercase',
                                    marginTop: 0
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            PROJECT INSTALL ADDRESS
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            {details?.community_address || ''}
                                            <br />
                                            {details?.community_city || ''} {details?.community_state || ''} {details?.community_zipcode || ''}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                    <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box
                            sx={{
                                textTransform: 'uppercase',
                                color: 'black',
                                minWidth: 1030,
                            }}
                        >
                            <table
                                className="sales-order-table"
                                style={{
                                    width: '100%',
                                    border: '1px solid black',
                                    borderCollapse: 'collapse',
                                    textTransform: 'uppercase',
                                    tableLayout: 'fixed'
                                }}
                            >
                                <colgroup>
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '42%' }} />
                                    <col style={{ width: '14%' }} /> {/* SKU */}
                                    <col style={{ width: '8%' }} />  {/* UNIT (blank) */}
                                    <col style={{ width: '12%' }} />  {/* QTY */}
                                    <col style={{ width: '10%' }} /> {/* UNIT PRICE */}
                                    <col style={{ width: '10%' }} /> {/* TOTAL */}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th
                                            colSpan={1}
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            &nbsp;
                                        </th>
                                        <th
                                            colSpan={1}
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            NOTES
                                        </th>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            PROJECT NAME
                                        </th>
                                        <th
                                            colSpan={3}
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            JOB NAME
                                        </th>
                                        <th
                                            style={{
                                                border: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: '#d9d9d9',
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            DUE DATE
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                borderTop: '1px solid black',
                                                borderBottom: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                            &nbsp;
                                        </td>
                                        <td
                                            colSpan={1}
                                            style={{
                                                borderTop: '1px solid black',
                                                borderBottom: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'pre-line',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                            {formattedNotes}
                                        </td>
                                        <td
                                            style={{
                                                borderTop: '1px solid black',
                                                borderBottom: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                            {details?.community_name || ''}
                                        </td>
                                        <td
                                            colSpan={3}
                                            style={{
                                                borderTop: '1px solid black',
                                                borderBottom: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                            {details?.name || ''}
                                        </td>
                                        <td
                                            style={{
                                                borderTop: '1px solid black',
                                                borderBottom: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '10px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                fontSize: '14px',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                            {formatDateOnly(details?.participation_ends || details?.installation_end_date)}
                                        </td>
                                    </tr>
                                    {(invoice?.cust_product?.length > 0) ? <>
                                            <tr>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    #
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    PRODUCTS DESCRIPTION
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    SKU
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    QTY
                                                </th>
                                                {canViewCosts && <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    RATE
                                                </th>}
                                                {canViewCosts && <th
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    TOTAL COST
                                                </th>}
                                            </tr>
                                            {groupedInvoiceDisplayRows.map((row, index) => {
                                                if (row?.__type === 'group') {
                                                    const representative = row?.representative || {}
                                                    const groupMeta = row?.groupMeta || {}
                                                    const groupName =
                                                        groupMeta.group_name ||
                                                        groupMeta.groupName ||
                                                        representative.group_name ||
                                                        `Group ${row?.groupId}`
                                                    const groupDescription =
                                                        groupMeta.group_description ||
                                                        groupMeta.groupDescription ||
                                                        representative.group_description ||
                                                        ''
                                                    const groupProductDisplayQty = Number(row?.groupProductDisplayQty ?? 1) || 1
                                                    const groupInstallDisplayQty = Number(row?.groupInstallDisplayQty ?? 1) || 1
                                                    const groupInstallSubtotal = Number(row?.groupInstallSubtotal ?? 0)
                                                    const groupHasInstall = Boolean(row?.groupHasInstall)
                                                    const displayGroupTotal = groupHasInstall
                                                        ? Number(row?.groupProductOnlyTotal ?? 0)
                                                        : Number(row?.totalValue ?? 0)
                                                    const unitEach = Number(row?.adjustedGroupRetailEach ?? 0)
                                                    const groupInstallUnitEach = Number(
                                                        row?.adjustedGroupInstallRetailEach ?? 0,
                                                    )
                                                    const groupInstallNotes = getGroupInstallNotesDisplay(row?.members)

                                                    const tdCommon = {
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase',
                                                    }

                                                    return (
                                                        <Fragment key={`inv-grp-${row.groupId}`}>
                                                            <tr>
                                                                <td style={{ ...tdCommon, textAlign: 'center' }}>{index + 1}</td>
                                                                <td style={{ ...tdCommon, textAlign: 'left' }}>
                                                                    <Typography variant="subtitle2" className="productName" sx={{  color: '#000000' }}>
                                                                        {groupName}
                                                                    </Typography>
                                                                    {/* {groupDescription ? (
                                                                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                                            {groupDescription}
                                                                        </Typography>
                                                                    ) : null} */}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        ...tdCommon,
                                                                        textAlign: 'center',
                                                                        whiteSpace: 'normal',
                                                                        wordBreak: 'break-all',
                                                                        overflowWrap: 'break-word',
                                                                        hyphens: 'auto',
                                                                        lineHeight: '18px',
                                                                    }}
                                                                >
                                                                    -
                                                                </td>
                                                                <td style={{ ...tdCommon, textAlign: 'center' }}>{groupProductDisplayQty}</td>
                                                                {canViewCosts && (
                                                                    <td style={{ ...tdCommon, textAlign: 'center', fontSize: '13px' }}>
                                                                        ${Number(unitEach || 0).toFixed(2)}
                                                                    </td>
                                                                )}
                                                                {canViewCosts && (
                                                                    <td colSpan={2} style={{ ...tdCommon, textAlign: 'center' }}>
                                                                        ${Number(displayGroupTotal || 0).toFixed(2)}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                            {groupHasInstall && showInstallPriceInPreview ? (
                                                                <tr>
                                                                    <td style={{ ...tdCommon, textAlign: 'center' }}>-</td>
                                                                    <td style={{ ...tdCommon, textAlign: 'left' }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: groupInstallNotes ? 'red' : 'inherit',
                                                                                whiteSpace: 'pre-wrap',
                                                                            }}
                                                                        >
                                                                            {groupInstallNotes || (canViewCosts ? 'Install Price' : 'Installation')}
                                                                        </Typography>
                                                                    </td>
                                                                    {canViewCosts ? (
                                                                        <>
                                                                            <td style={{ ...tdCommon, textAlign: 'center' }}>-</td>
                                                                            <td style={{ ...tdCommon, textAlign: 'center' }}>{groupInstallDisplayQty}</td>
                                                                            <td style={{ ...tdCommon, textAlign: 'center', fontSize: '13px' }}>
                                                                                ${Number(groupInstallUnitEach || 0).toFixed(2)}
                                                                            </td>
                                                                        </>
                                                                    ) : (
                                                                        <td style={{ ...tdCommon, textAlign: 'center' }}>-</td>
                                                                    )}
                                                                    {!canViewCosts ? (
                                                                        <td style={{ ...tdCommon, textAlign: 'center' }}>{groupInstallDisplayQty}</td>
                                                                    ) : null}
                                                                    {canViewCosts && (
                                                                        <td colSpan={2} style={{ ...tdCommon, textAlign: 'center' }}>
                                                                            ${Number(groupInstallSubtotal || 0).toFixed(2)}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            ) : null}
                                                        </Fragment>
                                                    )
                                                }

                                                const val = row
                                                const {
                                                    vid,
                                                    split_percentage,
                                                    wholesale_price,
                                                    facility_id,
                                                    incoming_split_percentage,
                                                    product_name,
                                                    product_notes,
                                                    material_notes,
                                                    install_notes,
                                                    vpt_number,
                                                    actual_retail_price,
                                                    install_cost,
                                                    install_qty,
                                                    install_quantity,
                                                    install_total,
                                                    unit_qty,
                                                    adjustment,
                                                    product_price,
                                                    material,
                                                    rfp,
                                                    product_retail_price,
                                                    product_sub_total,
                                                    quantity,
                                                    product_total,
                                                    discount,
                                                    unit,
                                                    locationQuantity,
                                                    markAdjType,
                                                    adj_value_type,
                                                    services = []
                                                } = val;

                                                const lineInstallQty =
                                                    Number(install_qty ?? install_quantity ?? locationQuantity ?? 1) || 1

                                                // Get labor install
                                                const labourService = services?.find(service => service.labour_cost_service_id === 10);
                                                const minusAmountLabourInstall = (labourService?.service_original_cost * labourService?.prQty) || 0;
                                                const labourQty = (labourService?.prQty || 0) * locationQuantity;
                                                const labourRate = labourService?.service_original_cost || 0;
                                                const labourTotal = labourQty * labourRate;
                                                const labourInitial = (labourService?.prQty || 0) * labourRate;

                                                // Unit name logic
                                                let unitData = UNITS?.filter((item) => item?.id == unit)
                                                let shortUnitName = unitData[0]?.short_name || 'Unit'
                                                let unitName = unitData[0]?.name

                                                // Match ViewOfficialRequest.js logic (base price only here)
                                                let SPMAAmount = 0
                                                if (markAdjType == 2 || markAdjType == 3) {
                                                    SPMAAmount = (product_retail_price || actual_retail_price || 0)
                                                } else {
                                                    SPMAAmount = (actual_retail_price || product_retail_price || 0)
                                                }
                                                if(material==1){
                                                    let newSPMA = Number(SPMAAmount) + (adj_value_type==1 ? Number(adjustment) : -Number(adjustment));
                                                    SPMAAmount = newSPMA
                                                    // console.log('SPMA-material==1',SPMA);
                                                    // console.log('SPMA-material==1',newSPMA);
                                                    // console.log('SPMA-material==1',adj_value_type);
                                                    // console.log('SPMA-material==1',adjustment);
                                                  }
                                                SPMAAmount = parseFloat(SPMAAmount)?.toFixed(2)

                                                // let theSubTotalAmount = markAdjType == 1 ? product_total : (SPMAAmount * (locationQuantity * quantity))
                                                let theSubTotalAmount = 0
                                                if (markAdjType == 1) {
                                                    theSubTotalAmount = product_total
                                                } else {
                                                    if (material == 1) {
                                                        theSubTotalAmount = (SPMAAmount * locationQuantity)
                                                    } else {
                                                        theSubTotalAmount = (SPMAAmount * (locationQuantity * quantity))
                                                    }
                                                }
                                                // Match ViewOfficialRequest.js (display price uses base retail)
                                                let SPMA = (product_retail_price || 0)
                                                SPMA = parseFloat(SPMA)?.toFixed(2)
                                                const designOrderUnitPrice =
                                                    Number(actual_retail_price) ||
                                                    Number(product_retail_price) ||
                                                    0
                                                const designOrderLineTotal =
                                                    product_sub_total != null
                                                        ? Number(product_sub_total)
                                                        : designOrderUnitPrice * (Number(locationQuantity) || 1)
                                                return (
                                                    <>
                                                        <tr key={vid}>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'left',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {product_notes? "":
                                                                <Typography variant='body2' className="productName" sx={{ color: '#000000' }}>
                                                                    {product_name}
                                                                </Typography>
                                                                }
                                                                {(material === 1 ? material_notes : product_notes) ? (
                                                                    <Typography variant='body2' className="productName" sx={{ color: '#000000' }}>
                                                                        {material === 1 ? material_notes : product_notes}
                                                                    </Typography>
                                                                ) : null}
                                                                {(material === 1 && rfp === 0 && details?.job_category_name != 'Time & Material')}
                                                                {(material === 1 && rfp === 1 && details?.job_category_name != 'Time & Material') ?
                                                                    <Typography variant='subtitle2'>(Material)</Typography>
                                                                    : null}
                                                                {(material === 1 && rfp === 0 && details?.job_category_name != 'Time & Material') ? <Typography variant='subtitle2'>(Addl. Material)</Typography> : ''}
                                                                {(material === 1 && rfp === 1 && details?.job_category_name != 'Time & Material') ?
                                                                    <Typography variant='subtitle2'>(Material)</Typography>
                                                                    : null}
                                                            </td>
                                                            <td style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'center',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                textTransform: 'uppercase',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-all',
                                                                overflowWrap: 'break-word',
                                                                hyphens: 'auto',
                                                                lineHeight: '18px'
                                                            }}>{vpt_number}</td>
                                                            {/* <TableCell align="center">{material === 1 ? '1' : quantity}({shortUnitName})</TableCell> */}
                                                            <td style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'center',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                textTransform: 'uppercase'
                                                            }}>{locationQuantity}</td>
                                                            {canViewCosts && <td style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'center',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '13px',
                                                                textTransform: 'uppercase'
                                                            }}>${designOrderUnitPrice.toFixed(2)}</td>}
                                                            {canViewCosts && (
                                                                <td
                                                                    colSpan={2}
                                                                    style={{
                                                                        border: '1px solid black',
                                                                        padding: '12px',
                                                                        textAlign: 'center',
                                                                        backgroundColor: 'white',
                                                                        color: 'black',
                                                                        textTransform: 'uppercase'
                                                                    }}
                                                                >
                                                                    ${designOrderLineTotal.toFixed(2)}
                                                                </td>
                                                            )}
                                                        </tr>
                                                        {parseFloat(install_cost) > 0 && showInstallPriceInPreview ? (
                                                            <tr key={index}>
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}>-</td>
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'left',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    <Typography sx={{ color: install_notes ? 'red' : 'inherit', whiteSpace: 'pre-wrap' }}>
                                                                        {install_notes || (canViewCosts ? 'Install Price' : 'Installation')}
                                                                    </Typography>
                                                                </td>
                                                                {canViewCosts ? (
                                                                    <>
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}>-</td>
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}>{lineInstallQty}</td>
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    fontSize: '13px',
                                                                    textTransform: 'uppercase'
                                                                }}>${install_cost || 0}</td>
                                                                    </>
                                                                ) : (
                                                                <td style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}>{lineInstallQty}</td>
                                                                )}
                                                                {canViewCosts && <td
                                                                    colSpan={2}
                                                                    style={{
                                                                        border: '1px solid black',
                                                                        padding: '12px',
                                                                        textAlign: 'center',
                                                                        backgroundColor: 'white',
                                                                        color: 'black',
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                    ${((install_cost || 0) * lineInstallQty).toFixed(2)}
                                                                </td>}

                                                            </tr>
                                                        ) : ''}
                                                    </>
                                                )
                                            }
                                            )}
                        </> : null}

                                {(additionalSelectedServices?.length > 0 || invoiceSelectedServices?.length > 0 || cartService?.length > 0) &&
                                        <tr>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    #
                                                </th>
                                                <th
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {(details?.job_category_name != 'Service only') ? "ADDITIONAL SERVICES" : 'SERVICES'}
                                                </th>
                                                {/* <th
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        fontSize: '13px',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    QTY
                                                </th>
                                                <th
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    RATE
                                                </th> */}
                                                <th
                                                    colSpan={4}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {canViewCosts ? 'TOTAL COSTS' : ''}
                                                </th>
                                            </tr>}
                                        {openInvoice && invoiceSelectedServices?.map((ser, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {index + 1}
                                                    </td>
                                                    <td
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'left',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                            {ser?.service_notes ?
                                                            <Stack direction={'column'}>
                                                                {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                            </Stack>
                                                            : (ser?.name || ser?.service_name)}
                                                        </Typography>
                                                    </td>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>{ser ? ser?.quantity?.toFixed(2) : 0}</td>
                                                    {canViewCosts && <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>${formatServiceHourlyCost(ser)}</td>}
                                                    {canViewCosts && <td
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>${ser ? ser?.total_cost?.toFixed(2) : 0}</td>}
                                                </tr>
                                            )
                                        }
                                        )}
                                        {openInvoice && [...(additionalSelectedServices || [])]
                                            .sort(
                                              (a, b) =>
                                                (Number(a?.product_index ?? a?.newIndexId ?? a?.indexId) || 0) -
                                                (Number(b?.product_index ?? b?.newIndexId ?? b?.indexId) || 0),
                                            )
                                            ?.filter(servItem => {
                                                return true;
                                            })
                                            ?.map((ser, index) => {
                                                console.log('additionalSelectedServices--', ser);
                                                return (
                                                    <tr key={index}>
                                                        <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {index + 1}
                                                        </td>
                                                        <td
                                                            colSpan={2}
                                                            style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'left',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                {ser?.service_notes ?
                                                                <Stack direction={'column'}>
                                                                    {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                    <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                                </Stack>
                                                                : (ser?.name || ser?.service_name)}
                                                            </Typography>
                                                        </td>
                                                        {/* <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>{ser ? ser?.quantity?.toFixed(2) : 0}</td>
                                                        <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>${ser ? ser?.unit_cost?.toFixed(2) : 0}</td> */}
                                                        {canViewCosts && <td
                                                            colSpan={4}
                                                            style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'center',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                textTransform: 'uppercase'
                                                            }}>${ser ? ser?.total_cost?.toFixed(2) : 0}</td>}
                                                    </tr>
                                                )
                                            }
                                            )}
                                        {openInvoice && cartService?.map((ser, index) => {
                                            console.log('cartService--', ser);
                                            ser.sellingPrice = details?.project_category == 13 ? ser.unit_cost : ser.sellingPrice || ser.unit_cost
                                            ser.originalMarkup = details?.project_category == 13 ? 0 : ser.originalMarkup || ser.product_markup
                                            return (
                                                <tr>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {index + 1}
                                                    </td>
                                                    <td
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'left',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                            {ser?.service_notes ?
                                                            <Stack direction={'column'}>
                                                                {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                            </Stack>
                                                            : (ser?.name || ser?.service_name)}
                                                        </Typography>
                                                    </td>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>{ser ? ser?.qty?.toFixed(2) : 0}</td>
                                                    {canViewCosts && <>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}> ${((ser.sellingPrice + (ser.sellingPrice * ser.originalMarkup / 100)))?.toFixed(2)}</td>
                                                    <td
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}> ${(ser.qty * (ser.sellingPrice + (ser.sellingPrice * ser.originalMarkup / 100)))?.toFixed(2)}</td>
                                                    </>}
                                                </tr>
                                            )
                                        }
                                        )}
                                    </tbody>
                                </table>
                        </Box>
                    </Grid>
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        borderBottom: '1px solid black',
                                        borderLeft: '1px solid black',
                                        borderRight: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '4%' }} />
                                        <col style={{ width: '42%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    <tbody>
                                        {canViewCosts && <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>PRODUCT</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${(
                                                    groupedInvoiceProductTotal != null
                                                        ? Number(groupedInvoiceProductTotal)
                                                        : invoice?.cust_product?.reduce((sum, val) => {
                                                            const {
                                                                product_retail_price = 0,
                                                                material = 0,
                                                                adjustment = 0,
                                                                markAdjType,
                                                                actual_retail_price,
                                                                locationQuantity = 1,
                                                                quantity = 1,
                                                                adj_value_type,
                                                            } = val;
                                                            const basePrice =
                                                                actual_retail_price !== 0 && actual_retail_price != null
                                                                    ? actual_retail_price
                                                                    : product_retail_price;
                                                            let SPMA =
                                                                markAdjType === 2 || markAdjType === 3 ? basePrice : basePrice;
                                                            if (material == 1) {
                                                                SPMA =
                                                                    Number(SPMA) +
                                                                    (adj_value_type == 1 ? Number(adjustment) : -Number(adjustment));
                                                            }
                                                            const effectiveTotal =
                                                                markAdjType === 1 && material === 0
                                                                    ? SPMA * (locationQuantity * quantity)
                                                                    : material === 1
                                                                      ? SPMA * locationQuantity
                                                                      : SPMA * (locationQuantity * quantity);
                                                            return sum + effectiveTotal;
                                                        }, 0) || 0
                                                ).toFixed(2)}
                                            </td>
                                        </tr>}

                                        {canViewCosts && ((labourInstallService?.length > 0 ||
                                            additionalSelectedServices?.some(x => x.labour_cost_service_id === 10 && (x.total_cost || 0) > 0) ||
                                            invoice?.cust_product?.some(val => parseFloat(val.install_cost || 0) > 0) ||
                                            (groupedInvoiceInstallTotal != null && Number(groupedInvoiceInstallTotal) > 0)
                                        ) ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>INSTALL</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${(
                                                        (groupedInvoiceInstallTotal != null
                                                            ? Number(groupedInvoiceInstallTotal)
                                                            : invoice?.cust_product?.reduce((sum, val) => {
                                                                const installTotal = parseFloat(val?.install_total || 0);
                                                                if (installTotal) return sum + installTotal;

                                                                const installCost = parseFloat(val?.install_cost || 0);
                                                                const installQty = parseFloat(
                                                                    val?.install_qty ?? val?.install_quantity ?? 1,
                                                                );
                                                                if (installCost) return sum + installCost * installQty;

                                                                return sum;
                                                            }, 0) || 0) +
                                                        (additionalSelectedServices?.reduce(
                                                            (s, x) => x.labour_cost_service_id === 10 ? s + (x.total_cost || 0) : s,
                                                            0
                                                        ) || 0)
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        ) : null)}

                                        {canViewCosts && <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>ADDITIONAL</span><span style={{ whiteSpace: 'nowrap' }}>SERVICES</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${(
                                                    (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (additionalSelectedServices?.reduce(
                                                        (s, x) => x.labour_cost_service_id === 10 ? s : s + (x.total_cost || 0),
                                                        0
                                                    ) || 0) +
                                                    (cartService?.reduce((s, x) => {
                                                        const sp = x.sellingPrice || x.unit_cost || 0;
                                                        const markup = x.originalMarkup || x.product_markup || 0;
                                                        const final = sp + (sp * markup / 100);
                                                        return s + ((x.qty || 0) * final);
                                                    }, 0) || 0)
                                                )?.toFixed(2)}
                                            </td>
                                        </tr>}

                                        {canViewCosts && <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px', }}>SUBTOTAL</td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${fUSPrice(subTotal || 0)}
                                            </td>
                                        </tr>}

                                        {canViewCosts && invoice?.extra_discount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>DISCOUNT</td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${invoice ? invoice?.extra_discount?.toFixed(2) : 0.00}
                                                </td>
                                            </tr>
                                        ) : null}

                                        {canViewCosts && invoice?.tax_amount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>SALES</span><span style={{ whiteSpace: 'nowrap' }}>TAX</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${invoice ? invoice?.tax_amount?.toFixed(2) : 0.00}
                                                </td>
                                            </tr>
                                        ) : null}

                                        {canViewCosts && <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>TOTAL</td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${fUSPrice(invoice?.total ? invoice?.total : 0)}
                                            </td>
                                        </tr>}

                                        {!canViewCosts || invoice?.project_category == 13 ? null : (
                                            <>
                                                {(userType != 3 && details?.job_category_name != 'Time & Material' && details?.job_category_name != 'No Charge' && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BILLING</span><span style={{ whiteSpace: 'nowrap' }}>TYPE</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            {details?.billing_type ? `${details?.billing_type} %` : "Nil"}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {(invoice?.total && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>MINIMUM</span><span style={{ whiteSpace: 'nowrap' }}>PAYABLE</span><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            ${(invoice?.total && details?.billing_type) ? parseFloat((invoice?.total * details?.billing_type) / 100).toFixed(2) : 0.00}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {details?.totalPaidAmount > 0 ? (
                                                    <>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span><span style={{ whiteSpace: 'nowrap' }}>PAID</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${details?.totalPaidAmount ? details?.totalPaidAmount?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BALANCE</span><span style={{ whiteSpace: 'nowrap' }}>DUE</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${details?.dueAmount ? parseFloat(details?.dueAmount <= 0 ? 0.00 : details?.dueAmount)?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : null}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                        </Box>
                    </Grid>
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                                {loading ? (
                                    <Skeleton variant="rectangular" width="100%" height={400} />
                                ) : (
                                    pdfs?.length >= 0 ? (
                                        <>
                                            {pdfs?.map((pdf, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        width: '100%',
                                                        pageBreakAfter: index < pdfs.length - 1 ? 'always' : 'auto'
                                                    }}
                                                >
                                                    {pdf.imagePaths?.map((imgPath, imgIndex) => (
                                                        <img
                                                            key={imgIndex}
                                                            src={imgPath.startsWith('http') ? imgPath : `${REST_API}${imgPath}`}
                                                            alt={`image-${imgIndex}`}
                                                            width="100%"
                                                            style={{
                                                                margin: 0,
                                                                padding: 0,
                                                                display: 'block',
                                                                verticalAlign: 'top',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                            {/* Always show signature once after all agreement content so it appears on both local and live */}
                                            <Box sx={{ minWidth: 1030, textAlign: 'left', marginTop: '16px' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '14px',
                                                        lineHeight: 1.5,
                                                        textTransform: 'none',
                                                        fontStyle: 'italic',
                                                        mb: 4,
                                                        color: 'black'
                                                    }}
                                                >
                                                    Acceptance of this design order indicates your agreement with our Terms and Conditions which will be provided upon request. (Sign below)
                                                </Typography>
                                                <Grid item xs={6} sm={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 3, gap: 1, color: 'black' }}>
                                                        <Typography sx={{ color: '#000000', fontSize: '14px', minWidth: '100px' }}>Signature:</Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2 }}>
                                                            <Box sx={{ borderBottom: '1px dashed black', minHeight: '22px', width: '200px', minWidth: '200px' }} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 3, gap: 1, color: 'black' }}>
                                                        <Typography sx={{ color: '#000000', fontSize: '14px', minWidth: '100px' }}>Print Name:</Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2 }}>
                                                            <Box sx={{ borderBottom: '1px dashed black', minHeight: '22px', width: '200px', minWidth: '200px' }} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, color: 'black' }}>
                                                        <Typography sx={{ color: '#000000', fontSize: '14px', minWidth: '100px' }}>Date:</Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2 }}>
                                                            <Box sx={{ borderBottom: '1px dashed black', minHeight: '22px', width: '200px', minWidth: '200px' }} />
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Box>
                                        </>
                                    ) : null
                                )}
                            </Box>
                        </Grid>
                </Grid>
            </Stack>
        </>
    )
})
export default DesignOrderInvoicePage;